import React, { useState, useEffect } from 'react';
import api from './api/axios';
import './App.css';
import HomePage from './pages/HomePage';
import AddJobPage from './pages/AddJobPage';
import AdminPage from './pages/AdminPage';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isPublicLoggedIn, setIsPublicLoggedIn] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userAvatar, setUserAvatar] = useState(null);
  const [locationFilter, setLocationFilter] = useState('Garth');
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Check for auth redirect query param first
    const params = new URLSearchParams(window.location.search);
    const authResult = params.get('auth');
    if (authResult) {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (authResult === 'failed') {
      setAuthError('Sign-in failed. Please use your @students.randolphschool.net Google account.');
    }
    checkLoginStatus(authResult);
  }, []);

  const checkLoginStatus = (authResult) => {
    api.get('/auth/me')
      .then(res => {
        const { loggedIn, email, avatar, isAdmin } = res.data;
        if (loggedIn) {
          setIsPublicLoggedIn(true);
          setUserEmail(email);
          setUserAvatar(avatar || null);
          if (isAdmin) {
            setIsAdminLoggedIn(true);
            // Redirect admin to admin page after login
            if (authResult === 'admin') setCurrentPage('admin');
          } else {
            // Redirect student to add-job page after login
            if (authResult === 'user') setCurrentPage('add-job');
          }
        }
      })
      .catch(() => {
        setIsPublicLoggedIn(false);
        setIsAdminLoggedIn(false);
        setUserEmail(null);
        setUserAvatar(null);
      });
  };

  const handleGoogleLogin = () => {
    const base = process.env.NODE_ENV === 'development' ? '/api' : '/hub/api';
    window.location.href = `${base}/auth/google`;
  };

  const handleMicrosoftLogin = () => {
    const base = process.env.NODE_ENV === 'development' ? '/api' : '/hub/api';
    window.location.href = `${base}/auth/microsoft`;
  };

  const handleLogout = () => {
    api.post('/auth/logout')
      .then(() => {
        setIsPublicLoggedIn(false);
        setIsAdminLoggedIn(false);
        setUserEmail(null);
        setUserAvatar(null);
        setCurrentPage('home');
      });
  };

  return (
    <div className="App">
      <header className="header">
        <h1>Randolph STEM Lab</h1>
        <nav className="nav">
          <button 
            className={currentPage === 'home' ? 'active' : ''}
            onClick={() => setCurrentPage('home')}
          >
            Home
          </button>
          {isPublicLoggedIn ? (
            <>
              <button 
                className={currentPage === 'add-job' ? 'active' : ''}
                onClick={() => setCurrentPage('add-job')}
              >
                Add Job
              </button>
              {isAdminLoggedIn && (
                <button 
                  className={currentPage === 'admin' ? 'active' : ''}
                  onClick={() => setCurrentPage('admin')}
                >
                  Admin
                </button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {userAvatar ? (
                  <img
                    src={userAvatar}
                    alt={userEmail || 'User'}
                    title={userEmail || ''}
                    style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', objectFit: 'cover' }}
                  />
                ) : (
                  <div title={userEmail || ''} style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>
                    {userEmail ? userEmail[0].toUpperCase() : '?'}
                  </div>
                )}
                <button onClick={handleLogout}>Logout</button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleGoogleLogin} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style={{ width: '16px', height: '16px' }} />
                  Student
                </button>
                <button onClick={handleMicrosoftLogin} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
                  Faculty
                </button>
              </div>
              {authError && (
                <span style={{ fontSize: '12px', color: '#ffcccc', maxWidth: '280px', textAlign: 'right' }}>{authError}</span>
              )}
            </div>
          )}
        </nav>
      </header>

      <main className="main-content">
        {currentPage === 'home' && (
          <HomePage 
            locationFilter={locationFilter}
            setLocationFilter={setLocationFilter}
            isLoggedIn={isPublicLoggedIn}
            onAddJobClick={() => {
              if (!isPublicLoggedIn) {
                handleGoogleLogin();
              } else {
                setCurrentPage('add-job');
              }
            }}
            onAdminClick={() => {
              if (isAdminLoggedIn) setCurrentPage('admin');
            }}
          />
        )}
        {currentPage === 'add-job' && isPublicLoggedIn && (
          <AddJobPage 
            onJobAdded={() => setCurrentPage('home')}
            setCurrentPage={setCurrentPage}
            returnPage={isAdminLoggedIn ? 'admin' : 'home'}
          />
        )}
        {currentPage === 'admin' && isAdminLoggedIn && (
          <AdminPage 
            locationFilter={locationFilter}
            setLocationFilter={setLocationFilter}
            setCurrentPage={setCurrentPage}
          />
        )}
      </main>
    </div>
  );
}

export default App;
